import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import InputField from "components/fields/InputField";
import { forgotPassword } from "api/auth";

const EMAIL_RULE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPassword(): JSX.Element {
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const value = email.trim();
    if (!value) {
      setFieldError("Email is required.");
      return;
    }
    if (!EMAIL_RULE.test(value)) {
      setFieldError("Enter a valid email address.");
      return;
    }
    setFieldError("");

    setSubmitting(true);
    setFormError("");
    try {
      const message = await forgotPassword(value);
      toast.success(message);
      setSent(true);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to send reset link";
      setFormError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="flex w-full flex-col">
        <h4 className="mb-2.5 text-xl font-bold tracking-wider text-[#222]">
          Check your email
        </h4>
        <p className="mb-4 ml-1 text-sm text-[#6D6D6D]">
          If an account exists for <span className="font-medium">{email.trim()}</span>,
          we&apos;ve sent a password reset link. The link expires in 15 minutes.
        </p>
        <p className="mb-6 ml-1 text-sm text-[#6D6D6D]">
          Didn&apos;t get it? Check your spam folder, or{" "}
          <button
            type="button"
            onClick={() => {
              setSent(false);
              setFormError("");
            }}
            className="font-medium text-[#222] underline hover:text-[#333]"
          >
            try again
          </button>
          . If it still doesn&apos;t arrive, contact support.
        </p>
        <div className="mt-2 text-center">
          <Link
            to="/auth/sign-in"
            className="text-sm font-medium text-[#222] hover:text-[#333]"
          >
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="flex w-full flex-col">
      <h4 className="mb-2.5 text-xl font-bold tracking-wider text-[#222]">
        Forgot password
      </h4>
      <p className="mb-6 ml-1 text-sm text-[#6D6D6D]">
        Enter your account email and we&apos;ll send you a reset link.
      </p>

      {!!formError && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-[#FFD5D8] bg-[#FFF1F2] px-3 py-2 text-[13px] text-[#FB2C36]"
        >
          {formError}
        </div>
      )}

      <InputField
        variant="auth"
        extra="mb-3"
        label="Email*"
        placeholder="mail@example.com"
        id="forgot-email"
        name="email"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        state={fieldError ? "error" : undefined}
        aria-invalid={!!fieldError}
      />
      {fieldError ? (
        <p className="-mt-2 mb-2 text-xs text-red-600" role="alert">
          {fieldError}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={submitting}
        aria-busy={submitting}
        className="mt-2 w-full rounded-md bg-[#222] py-2.5 text-xs font-semibold text-white transition duration-200 hover:bg-[#333] active:bg-[#111] disabled:opacity-60"
      >
        {submitting ? "Sending..." : "Send reset link"}
      </button>

      <div className="mt-4 text-center">
        <Link
          to="/auth/sign-in"
          className="text-sm font-medium text-[#222] hover:text-[#333]"
        >
          Back to Sign In
        </Link>
      </div>
    </form>
  );
}
